"use client";

import { useIsGrownup } from "@/hooks/use-is-grownup";
import {
  type AbandonReason,
  captureSearchAbandoned,
  captureSearchPerformed,
  captureSearchResultClicked,
} from "@/lib/analytics/search-events";
import { normalizeQuery } from "@/lib/analytics/search-normalize";
import { SEARCH_SETTLE_DEBOUNCE_MS, shouldEmitSettledQuery } from "@/lib/analytics/search-settle";
import type { PageOriginContext } from "@/lib/page-origin-context";
import type { GameWithRelations } from "@/types/game";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef } from "react";

export type UseSearchAnalyticsOptions = {
  searchSessionId: string | null;
  rawQuery: string;
  page: string;
  pageOrigin: PageOriginContext | null;
  uncappedResultCount: number;
  isComposing: boolean;
  /** True when the search catalog has loaded or definitively failed. */
  resultsReady: boolean;
  /** True while the search dropdown is open with a query (defer/cancel settle when false). */
  active: boolean;
};

function buildContext(
  searchSessionId: string,
  rawQuery: string,
  page: string,
  pageOrigin: PageOriginContext | null,
  isGrownup: boolean
) {
  return {
    search_session_id: searchSessionId,
    rawQuery,
    page,
    page_origin: pageOrigin,
    is_grownup: isGrownup,
  };
}

/**
 * PostHog analytics for header search: settled queries, result clicks, and abandons.
 * Raw query text is captured only for self-identified grownups (PII-redacted in the event helper).
 */
export function useSearchAnalytics({
  searchSessionId,
  rawQuery,
  page,
  pageOrigin,
  uncappedResultCount,
  isComposing,
  resultsReady,
  active,
}: UseSearchAnalyticsOptions) {
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  posthogRef.current = posthog;
  const isGrownup = useIsGrownup();

  const lastEmittedNormalizedRef = useRef<string | null>(null);
  const lastSettledRawQueryRef = useRef("");
  const lastSettledResultCountRef = useRef(0);
  const selectionMadeRef = useRef(false);
  const hasPerformedRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSettleQueryRef = useRef<string | null>(null);
  const searchSessionIdRef = useRef(searchSessionId);
  const pageRef = useRef(page);
  const pageOriginRef = useRef(pageOrigin);
  const uncappedResultCountRef = useRef(uncappedResultCount);
  const isGrownupRef = useRef(isGrownup);
  const resultsReadyRef = useRef(resultsReady);
  const activeRef = useRef(active);

  searchSessionIdRef.current = searchSessionId;
  pageRef.current = page;
  pageOriginRef.current = pageOrigin;
  uncappedResultCountRef.current = uncappedResultCount;
  isGrownupRef.current = isGrownup;
  resultsReadyRef.current = resultsReady;
  activeRef.current = active;

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const emitSearchPerformed = useCallback((query: string, resultCount: number) => {
    const sessionId = searchSessionIdRef.current;
    if (!sessionId) return;

    const normalized = normalizeQuery(query);
    if (!shouldEmitSettledQuery(query, lastEmittedNormalizedRef.current)) {
      return;
    }

    lastEmittedNormalizedRef.current = normalized;
    lastSettledRawQueryRef.current = query.trim();
    lastSettledResultCountRef.current = resultCount;
    hasPerformedRef.current = true;

    captureSearchPerformed(
      posthogRef.current?.capture.bind(posthogRef.current),
      buildContext(sessionId, query, pageRef.current, pageOriginRef.current, isGrownupRef.current),
      {
        result_count: resultCount,
        had_results: resultCount > 0,
      }
    );
  }, []);

  const tryEmitSettledQuery = useCallback(
    (query: string) => {
      if (!activeRef.current || !searchSessionIdRef.current) {
        pendingSettleQueryRef.current = null;
        return;
      }

      if (!resultsReadyRef.current) {
        pendingSettleQueryRef.current = query;
        return;
      }

      pendingSettleQueryRef.current = null;
      emitSearchPerformed(query, uncappedResultCountRef.current);
    },
    [emitSearchPerformed]
  );

  const flushPendingSettle = useCallback(() => {
    const pendingQuery = pendingSettleQueryRef.current;
    if (!pendingQuery || !activeRef.current || !resultsReadyRef.current) {
      return;
    }

    pendingSettleQueryRef.current = null;
    emitSearchPerformed(pendingQuery, uncappedResultCountRef.current);
  }, [emitSearchPerformed]);

  const trackAbandon = useCallback(
    (
      reason: AbandonReason,
      pageOverride?: { page: string; pageOrigin: PageOriginContext | null }
    ) => {
      const sessionId = searchSessionIdRef.current;
      if (!sessionId || selectionMadeRef.current || !hasPerformedRef.current) {
        return false;
      }

      hasPerformedRef.current = false;
      lastEmittedNormalizedRef.current = null;

      captureSearchAbandoned(
        posthogRef.current?.capture.bind(posthogRef.current),
        buildContext(
          sessionId,
          lastSettledRawQueryRef.current,
          pageOverride?.page ?? pageRef.current,
          pageOverride ? pageOverride.pageOrigin : pageOriginRef.current,
          isGrownupRef.current
        ),
        {
          result_count: lastSettledResultCountRef.current,
          had_results: lastSettledResultCountRef.current > 0,
          abandon_reason: reason,
        }
      );
      return true;
    },
    []
  );

  const trackAbandonRef = useRef(trackAbandon);
  trackAbandonRef.current = trackAbandon;

  const trackResultClick = useCallback(
    (game: GameWithRelations, position: number) => {
      const sessionId = searchSessionIdRef.current;
      if (!sessionId) return;

      // Guard against rapid double-clicks emitting two terminal events for one session.
      if (selectionMadeRef.current) return;
      selectionMadeRef.current = true;

      const queryAtClick = rawQuery.trim();
      if (queryAtClick && shouldEmitSettledQuery(queryAtClick, lastEmittedNormalizedRef.current)) {
        emitSearchPerformed(queryAtClick, uncappedResultCountRef.current);
      }

      captureSearchResultClicked(
        posthogRef.current?.capture.bind(posthogRef.current),
        buildContext(
          sessionId,
          queryAtClick || lastSettledRawQueryRef.current,
          pageRef.current,
          pageOriginRef.current,
          isGrownupRef.current
        ),
        {
          game_id: game.id,
          game_slug: game.slug,
          position,
          result_count: uncappedResultCountRef.current,
        }
      );
    },
    [rawQuery, emitSearchPerformed]
  );

  useEffect(() => {
    if (!searchSessionId) {
      clearDebounce();
      return;
    }

    lastEmittedNormalizedRef.current = null;
    lastSettledRawQueryRef.current = "";
    lastSettledResultCountRef.current = 0;
    selectionMadeRef.current = false;
    hasPerformedRef.current = false;
    pendingSettleQueryRef.current = null;
  }, [searchSessionId, clearDebounce]);

  useEffect(() => {
    if (!searchSessionId || !active || isComposing) {
      clearDebounce();
      if (!active) {
        pendingSettleQueryRef.current = null;
      }
      return;
    }

    pendingSettleQueryRef.current = null;
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      tryEmitSettledQuery(rawQuery);
    }, SEARCH_SETTLE_DEBOUNCE_MS);

    return clearDebounce;
  }, [rawQuery, searchSessionId, isComposing, active, clearDebounce, tryEmitSettledQuery]);

  useEffect(() => {
    if (!active || !resultsReady) return;
    flushPendingSettle();
  }, [resultsReady, active, uncappedResultCount, flushPendingSettle]);

  const previousPageRef = useRef(page);
  const previousPageOriginRef = useRef(pageOrigin);

  useEffect(() => {
    const previousPage = previousPageRef.current;
    if (previousPage === page) return;

    trackAbandonRef.current("route_change", {
      page: previousPage,
      pageOrigin: previousPageOriginRef.current,
    });

    previousPageRef.current = page;
    previousPageOriginRef.current = pageOriginRef.current;
  }, [page]);

  useEffect(() => {
    return () => {
      clearDebounce();
      trackAbandonRef.current("route_change");
    };
  }, [clearDebounce]);

  return { trackResultClick, trackAbandon };
}

export type { AbandonReason };
